import {compareDesc} from 'date-fns';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {parseDate} from 'shared/web';

import {
  ListSentToastNotificationsApiResponse,
  ListSentToastNotificationsApiArg,
  PatchUnreadSentToastNotificationsApiArg,
  PatchSentToastNotificationApiResponse,
  PatchSentToastNotificationApiArg,
  GetSentToastNotificationApiResponse,
  GetSentToastNotificationApiArg,
  RenderCustomerEmailTemplateApiArg,
  RenderCustomerEmailTemplateApiResponse,
  RenderCustomerEmailTemplateExampleApiArg,
  RenderCustomerEmailTemplateExampleApiResponse,
  RenderCustomerSmsTemplateApiArg,
  RenderCustomerSmsTemplateApiResponse,
  RenderCustomerSmsTemplateExampleApiArg,
  RenderCustomerSmsTemplateExampleApiResponse,
  RenderBusinessCaseEmailTemplateApiArg,
  RenderBusinessCaseEmailTemplateApiResponse,
  RenderBusinessCaseSmsTemplateApiArg,
  RenderBusinessCaseSmsTemplateApiResponse,
  ListContextParametersApiArg,
  ListContextParametersApiResponse,
  CreateMessageTemplateApiArg,
  CreateMessageTemplateApiResponse,
  GetMessageTemplateApiArg,
  GetMessageTemplateApiResponse,
  PatchMessageTemplateApiArg,
  PatchMessageTemplateApiResponse,
  DeleteMessageTemplateApiArg,
  DeleteMessageTemplateApiResponse,
  GetContextTemplateListApiArg,
  GetContextTemplateListApiResponse,
  ListNotificationDefinitionsApiArg,
  ListNotificationDefinitionsApiResponse,
  GetToastNotificationExampleApiArg,
  GetToastNotificationExampleApiResponse,
  PreviewEmailNotificationApiResponse,
  PreviewEmailNotificationApiArg,
  RenderServiceOrderEmailTemplateApiArg,
  RenderServiceOrderEmailTemplateApiResponse,
  RenderServiceOrderEmailTemplateExampleApiArg,
  RenderServiceOrderEmailTemplateExampleApiResponse,
  RenderServiceOrderSmsTemplateApiArg,
  RenderServiceOrderSmsTemplateApiResponse,
  RenderServiceOrderSmsTemplateExampleApiArg,
  RenderServiceOrderSmsTemplateExampleApiResponse,
  PatchTenantNotificationSettingsApiResponse,
  PatchTenantNotificationSettingsApiArg,
  PatchUserNotificationSettingsApiArg,
  PatchUserNotificationSettingsApiResponse,
  GetNotificationSettingsApiArg,
  GetNotificationSettingsApiResponse,
  GetNotificationDefinitionsApiResponse,
  GetNotificationDefinitionsApiArg,
  RenderEmailTemplateResponseBody,
  RenderSmsTemplateResponseBody,
  CreateSignatureApiArg,
  CreateSignatureApiResponse,
  ListSignaturesApiArg,
  ListSignaturesApiResponse,
  RemoveSignatureApiArg,
  RemoveSignatureApiResponse,
  UpdateSignatureApiArg,
  UpdateSignatureApiResponse,
  SendCustomerEmailApiArg,
  SendCustomerEmailApiResponse,
  CreateEmailSmtpCredentialsApiArg,
  CreateEmailSmtpCredentialsApiResponse,
  CreateMicrosoftOAuth2CredentialsApiArg,
  CreateMicrosoftOAuth2CredentialsApiResponse,
  DeleteEmailCredentialsApiArg,
  DeleteEmailCredentialsApiResponse,
  DiscoverEmailProviderApiArg,
  DiscoverEmailProviderApiResponse,
  ListEmailCredentialsApiArg,
  ListEmailCredentialsApiResponse,
  ListEmailProviderApiArg,
  ListEmailProviderApiResponse,
  PatchEmailSmtpCredentialsApiArg,
  PatchEmailSmtpCredentialsApiResponse,
  GetLastKnownEmailCredentialsApiArg,
  GetLastKnownEmailCredentialsApiResponse,
  StartMicrosoftOAuth2ApiArg,
  StartMicrosoftOAuth2ApiResponse,
  CompleteMicrosoftOAuth2ApiArg,
  CompleteMicrosoftOAuth2ApiResponse,
  SendCustomerSmsApiArg,
  SendCustomerSmsApiResponse,
  RenderCustomerMessageEmailTemplateApiArg,
  RenderCustomerMessageEmailTemplateApiResponse,
  RenderCustomerMessageSmsTemplateApiArg,
  RenderCustomerMessageSmsTemplateApiResponse,
  RenderRentalOrderEmailTemplateApiArg,
  RenderRentalOrderEmailTemplateApiResponse,
  RenderRentalOrderEmailTemplateExampleApiArg,
  RenderRentalOrderEmailTemplateExampleApiResponse,
  RenderRentalOrderSmsTemplateApiArg,
  RenderRentalOrderSmsTemplateApiResponse,
  RenderRentalOrderSmsTemplateExampleApiArg,
  RenderRentalOrderSmsTemplateExampleApiResponse,
  RenderVehicleEmailTemplateApiArg,
  RenderVehicleEmailTemplateApiResponse,
  RenderVehicleEmailTemplateExampleApiArg,
  RenderVehicleEmailTemplateExampleApiResponse,
  RenderVehicleSmsTemplateApiArg,
  RenderVehicleSmsTemplateApiResponse,
  RenderVehicleSmsTemplateExampleApiArg,
  RenderVehicleSmsTemplateExampleApiResponse,
  GetCustomerMessageDefinitionApiArg,
  GetCustomerMessageDefinitionApiResponse,
  ListLatestSchedulerRunsApiArg,
  ListLatestSchedulerRunsApiResponse,
  DeleteLatestSchedulerRunApiArg,
  DeleteLatestSchedulerRunApiResponse,
  RunSchedulerApiArg,
  RunSchedulerApiResponse,
  PatchOAuthEmailCredentialsApiArg,
  PatchOAuthEmailCredentialsApiResponse,
  SendWhatsAppContentMessageApiArg,
  SendWhatsAppContentMessageApiResponse,
  RenderCustomerMessageEmailTemplateV2ApiArg,
  RenderCustomerMessageEmailTemplateV2ResponseBody,
  GetMessagingAllowlistApiArg,
  GetMessagingAllowlistApiResponse,
  UpdateMessagingAllowlistApiArg,
  UpdateMessagingAllowlistApiResponse,
  CreateGoogleUserMailboxApiArg,
  CreateGoogleUserMailboxApiResponse,
  ListUserMailboxesApiArg,
  ListUserMailboxesApiResponse,
  ListCustomerMessagesApiArg,
  ListCustomerMessagesApiResponse,
  CreateMicrosoftUserMailboxApiArg,
  CreateMicrosoftUserMailboxApiResponse,
  CreateImapUserMailboxApiArg,
  CreateImapUserMailboxApiResponse,
  UpdateImapUserMailboxApiArg,
  UpdateImapUserMailboxApiResponse,
  DeleteUserMailboxApiArg,
  DeleteUserMailboxApiResponse,
  ReauthenticateUserMailboxApiArg,
  ReauthenticateUserMailboxApiResponse,
  GetUserMailboxConnectionStatusApiArg,
  GetUserMailboxConnectionStatusApiResponse,
} from './types';

export const messagingApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    listSentToastNotifications: build.query<
      ListSentToastNotificationsApiResponse,
      ListSentToastNotificationsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/toast-notification`,
        params: {limit: queryArg.limit, after: queryArg.after},
      }),
      // BE doesn't keep stable order within notifications with the same createdAt
      transformResponse: (response: ListSentToastNotificationsApiResponse) => ({
        ...response,
        notifications: response.notifications.sort((a, b) => {
          const dateComparison = compareDesc(parseDate(a.createdAt), parseDate(b.createdAt));
          if (dateComparison === 0) {
            return b.id.localeCompare(a.id);
          }
          return dateComparison;
        }),
      }),
      providesTags: (result) =>
        result?.notifications
          ? [
              ...result.notifications.map(({id}) => ({type: 'Notification' as const, id})),
              {type: 'Notification', id: 'LIST'},
            ]
          : [{type: 'Notification', id: 'LIST'}],
    }),
    listContextParameters: build.query<
      ListContextParametersApiResponse,
      ListContextParametersApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/${queryArg.context}/parameter`,
      }),
    }),
    getSentToastNotification: build.query<
      GetSentToastNotificationApiResponse,
      GetSentToastNotificationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/toast-notification/${queryArg.id}`,
      }),
    }),

    getSentBackgroundNotification: build.query<
      GetSentToastNotificationApiResponse,
      GetSentToastNotificationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/background-notification/${queryArg.id}`,
      }),
    }),

    patchUnreadSentToastNotifications: build.mutation<
      unknown,
      PatchUnreadSentToastNotificationsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/toast-notification/unread`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: [{type: 'Notification', id: 'LIST'}],
    }),
    sendCustomerEmail: build.mutation<SendCustomerEmailApiResponse, SendCustomerEmailApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/customer-email`,
        method: 'POST',
        body: queryArg.sendCustomerEmailRequestBody,
      }),
      invalidatesTags: [
        'BusinessCaseActivity',
        'InterestActivity',
        'WorkshopInterestActivity',
        'CustomerMessage',
      ],
    }),
    patchSentToastNotification: build.mutation<
      PatchSentToastNotificationApiResponse,
      PatchSentToastNotificationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/toast-notification/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: [{type: 'Notification', id: 'LIST'}],
    }),
    createTemplate: build.mutation<CreateMessageTemplateApiResponse, CreateMessageTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/template/${queryArg.templateType}`,
        method: 'POST',
        body: queryArg.createTemplateRequestBody,
      }),
      invalidatesTags: ['MessageTemplates'],
    }),
    getTemplate: build.query<GetMessageTemplateApiResponse, GetMessageTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/template/${queryArg.templateType}/${queryArg.templateId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {
          type: 'MessageTemplate',
          id: queryArg.templateId,
        },
      ],
    }),
    patchTemplate: build.mutation<PatchMessageTemplateApiResponse, PatchMessageTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/template/${queryArg.templateType}/${queryArg.templateId}`,
        method: 'PATCH',
        body: queryArg.patchTemplateRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'MessageTemplate',
          id: queryArg.templateId,
        },
        'MessageTemplates',
      ],
    }),
    deleteTemplate: build.mutation<DeleteMessageTemplateApiResponse, DeleteMessageTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/template/${queryArg.templateType}/${queryArg.templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MessageTemplates'],
    }),
    getContextTemplateList: build.query<
      GetContextTemplateListApiResponse,
      GetContextTemplateListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/template`,
        params: {type: queryArg.type, context: queryArg.context},
      }),
      providesTags: ['MessageTemplates'],
    }),
    renderCustomerEmailTemplate: build.mutation<
      RenderCustomerEmailTemplateApiResponse,
      RenderCustomerEmailTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/customer/render-email`,
        method: 'POST',
        body: queryArg.renderCustomerEmailTemplateRequestBody,
      }),
    }),
    renderCustomerEmailTemplateExample: build.mutation<
      RenderCustomerEmailTemplateExampleApiResponse,
      RenderCustomerEmailTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/customer/render-email-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderCustomerSmsTemplate: build.mutation<
      RenderCustomerSmsTemplateApiResponse,
      RenderCustomerSmsTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/customer/render-sms`,
        method: 'POST',
        body: queryArg.renderCustomerSmsTemplateRequestBody,
      }),
    }),
    renderCustomerSmsTemplateExample: build.mutation<
      RenderCustomerSmsTemplateExampleApiResponse,
      RenderCustomerSmsTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/customer/render-sms-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderServiceOrderEmailTemplate: build.mutation<
      RenderServiceOrderEmailTemplateApiResponse,
      RenderServiceOrderEmailTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/service-order/render-email`,
        method: 'POST',
        body: queryArg.renderServiceOrderEmailTemplateRequestBody,
      }),
    }),
    renderServiceOrderEmailTemplateExample: build.mutation<
      RenderServiceOrderEmailTemplateExampleApiResponse,
      RenderServiceOrderEmailTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/service-order/render-email-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderServiceOrderSmsTemplate: build.mutation<
      RenderServiceOrderSmsTemplateApiResponse,
      RenderServiceOrderSmsTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/service-order/render-sms`,
        method: 'POST',
        body: queryArg.renderServiceOrderSmsTemplateRequestBody,
      }),
    }),
    renderServiceOrderSmsTemplateExample: build.mutation<
      RenderServiceOrderSmsTemplateExampleApiResponse,
      RenderServiceOrderSmsTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/service-order/render-sms-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderBusinessCaseEmailTemplate: build.mutation<
      RenderBusinessCaseEmailTemplateApiResponse,
      RenderBusinessCaseEmailTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/business-case/render-email`,
        method: 'POST',
        body: queryArg.renderBusinessCaseEmailTemplateRequestBody,
      }),
    }),
    renderBusinessCaseSmsTemplate: build.mutation<
      RenderBusinessCaseSmsTemplateApiResponse,
      RenderBusinessCaseSmsTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/business-case/render-sms`,
        method: 'POST',
        body: queryArg.renderBusinessCaseSmsTemplateRequestBody,
      }),
    }),
    renderBusinessCaseEmailTemplateExample: build.mutation<
      RenderEmailTemplateResponseBody,
      RenderCustomerEmailTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/business-case/render-email-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderBusinessCaseSmsTemplateExample: build.mutation<
      RenderSmsTemplateResponseBody,
      RenderCustomerSmsTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/business-case/render-sms-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    listNotificationDefinitions: build.query<
      ListNotificationDefinitionsApiResponse,
      ListNotificationDefinitionsApiArg
    >({
      query: () => ({
        url: `/dms/v1/messaging/notification/definition`,
      }),
    }),
    getNotificationDefinitions: build.query<
      GetNotificationDefinitionsApiResponse,
      GetNotificationDefinitionsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/definition/${queryArg.id}`,
      }),
    }),
    getToastNotificationExample: build.query<
      GetToastNotificationExampleApiResponse,
      GetToastNotificationExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/definition/${queryArg.id}/example-toast-notification`,
      }),
    }),
    previewEmailNotification: build.query<
      PreviewEmailNotificationApiResponse,
      PreviewEmailNotificationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/definition/${queryArg.id}/example-email-notification/${queryArg.locale}`,
        responseHandler: async (response) => {
          const text = await response.text();
          return text;
        },
      }),
    }),
    patchTenantNotificationSettings: build.mutation<
      PatchTenantNotificationSettingsApiResponse,
      PatchTenantNotificationSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/definition/${queryArg.id}/tenant-settings`,
        method: 'PATCH',
        body: queryArg.patchTenantNotificationSettingsRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'notificationSettings', id: queryArg.id},
      ],
    }),
    patchUserNotificationSettings: build.mutation<
      PatchUserNotificationSettingsApiResponse,
      PatchUserNotificationSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/definition/${queryArg.id}/user-settings`,
        method: 'PATCH',
        body: queryArg.patchUserNotificationSettingsRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'notificationSettings', id: queryArg.id},
      ],
    }),
    getNotificationSettings: build.query<
      GetNotificationSettingsApiResponse,
      GetNotificationSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/notification/definition/${queryArg.id}/settings`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'notificationSettings', id: queryArg.id}],
    }),
    createSignature: build.mutation<CreateSignatureApiResponse, CreateSignatureApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/signature`,
        method: 'POST',
        body: queryArg.updateSignatureRequestBody,
      }),
      invalidatesTags: ['EmailSignature'],
    }),
    listSignatures: build.query<ListSignaturesApiResponse, ListSignaturesApiArg>({
      query: () => ({
        url: `/dms/v1/messaging/email/signature`,
      }),
      providesTags: ['EmailSignature'],
    }),
    removeSignature: build.mutation<RemoveSignatureApiResponse, RemoveSignatureApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/signature/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailSignature'],
    }),
    updateSignature: build.mutation<UpdateSignatureApiResponse, UpdateSignatureApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/signature/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateSignatureRequestBody,
      }),
      invalidatesTags: ['EmailSignature'],
    }),
    discoverEmailProvider: build.mutation<
      DiscoverEmailProviderApiResponse,
      DiscoverEmailProviderApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials/provider/discover`,
        method: 'POST',
        body: queryArg.discoverEmailProviderRequestBody,
      }),
    }),
    listEmailProvider: build.query<ListEmailProviderApiResponse, ListEmailProviderApiArg>({
      query: () => ({
        url: `/dms/v1/messaging/email/credentials/provider`,
      }),
    }),
    startMicrosoftOAuth2: build.mutation<
      StartMicrosoftOAuth2ApiResponse,
      StartMicrosoftOAuth2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/auth/microsoft/oauth2/start`,
        method: 'POST',
        body: queryArg.startMicrosoftOAuth2RequestBody,
      }),
    }),
    completeMicrosoftOAuth2: build.mutation<
      CompleteMicrosoftOAuth2ApiResponse,
      CompleteMicrosoftOAuth2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/auth/microsoft/oauth2/complete`,
        method: 'POST',
        body: queryArg.completeMicrosoftOAuth2RequestBody,
      }),
    }),
    createMicrosoftOAuth2Credentials: build.mutation<
      CreateMicrosoftOAuth2CredentialsApiResponse,
      CreateMicrosoftOAuth2CredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials/oauth/microsoft`,
        method: 'POST',
        body: queryArg.createMicrosoftOAuth2CredentialsRequestBody,
      }),
      invalidatesTags: ['EmailCredentials'],
    }),
    patchOAuthEmailCredentials: build.mutation<
      PatchOAuthEmailCredentialsApiResponse,
      PatchOAuthEmailCredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials/oauth/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.patchOAuthEmailCredentialsRequestBody,
      }),
      invalidatesTags: ['EmailCredentials'],
    }),
    createEmailSmtpCredentials: build.mutation<
      CreateEmailSmtpCredentialsApiResponse,
      CreateEmailSmtpCredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials/smtp`,
        method: 'POST',
        body: queryArg.createSmtpCredentialsRequestBody,
      }),
      invalidatesTags: ['EmailCredentials'],
    }),
    deleteEmailCredentials: build.mutation<
      DeleteEmailCredentialsApiResponse,
      DeleteEmailCredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailCredentials'],
    }),
    listEmailCredentials: build.query<ListEmailCredentialsApiResponse, ListEmailCredentialsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials`,
        params: {ownerType: queryArg.ownerType},
      }),
      providesTags: ['EmailCredentials'],
    }),
    patchEmailSmtpCredentials: build.mutation<
      PatchEmailSmtpCredentialsApiResponse,
      PatchEmailSmtpCredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/credentials/smtp/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.patchSmtpCredentialsRequestBody,
      }),
      invalidatesTags: ['EmailCredentials'],
    }),
    getLastKnownSmtpCredentials: build.query<
      GetLastKnownEmailCredentialsApiResponse,
      GetLastKnownEmailCredentialsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/customer-message/${queryArg.customerMessageId}/email/last-used-credentials`,
      }),
      keepUnusedDataFor: 0,
    }),
    sendCustomerSms: build.mutation<SendCustomerSmsApiResponse, SendCustomerSmsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/customer-sms`,
        method: 'POST',
        body: queryArg.sendCustomerSmsRequestBody,
      }),
      invalidatesTags: [
        'BusinessCaseActivity',
        'InterestActivity',
        'WorkshopInterestActivity',
        'CustomerMessage',
      ],
    }),
    sendWhatsAppContentMessage: build.mutation<
      SendWhatsAppContentMessageApiResponse,
      SendWhatsAppContentMessageApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/whatsapp/content`,
        method: 'POST',
        body: queryArg.sendWhatsAppContentMessageRequestBody,
      }),
      invalidatesTags: [
        'BusinessCaseActivity',
        'InterestActivity',
        'WorkshopInterestActivity',
        'CustomerMessage',
      ],
    }),
    renderCustomerMessageEmailTemplate: build.mutation<
      RenderCustomerMessageEmailTemplateApiResponse,
      RenderCustomerMessageEmailTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/customer-message/${queryArg.customerMessageId}/email/render`,
        method: 'POST',
        body: queryArg.renderCustomerMessageRequestBody,
      }),
    }),
    renderCustomerMessageSmsTemplate: build.mutation<
      RenderCustomerMessageSmsTemplateApiResponse,
      RenderCustomerMessageSmsTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/customer-message/${queryArg.customerMessageId}/sms/render`,
        method: 'POST',
        body: queryArg.renderCustomerMessageRequestBody,
      }),
    }),
    renderRentalOrderEmailTemplate: build.mutation<
      RenderRentalOrderEmailTemplateApiResponse,
      RenderRentalOrderEmailTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/rental-order/render-email`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderRentalOrderEmailTemplateExample: build.mutation<
      RenderRentalOrderEmailTemplateExampleApiResponse,
      RenderRentalOrderEmailTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/rental-order/render-email-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderRentalOrderSmsTemplate: build.mutation<
      RenderRentalOrderSmsTemplateApiResponse,
      RenderRentalOrderSmsTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/rental-order/render-sms`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderRentalOrderSmsTemplateExample: build.mutation<
      RenderRentalOrderSmsTemplateExampleApiResponse,
      RenderRentalOrderSmsTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/rental-order/render-sms-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderVehicleEmailTemplate: build.mutation<
      RenderVehicleEmailTemplateApiResponse,
      RenderVehicleEmailTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/vehicle/render-email`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderVehicleEmailTemplateExample: build.mutation<
      RenderVehicleEmailTemplateExampleApiResponse,
      RenderVehicleEmailTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/vehicle/render-email-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderVehicleSmsTemplate: build.mutation<
      RenderVehicleSmsTemplateApiResponse,
      RenderVehicleSmsTemplateApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/vehicle/render-sms`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    renderVehicleSmsTemplateExample: build.mutation<
      RenderVehicleSmsTemplateExampleApiResponse,
      RenderVehicleSmsTemplateExampleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/context/vehicle/render-sms-example`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    getCustomerMessageDefinition: build.query<
      GetCustomerMessageDefinitionApiResponse,
      GetCustomerMessageDefinitionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/customer-message/${queryArg.id}/definition`,
      }),
    }),
    listLatestSchedulerRuns: build.query<
      ListLatestSchedulerRunsApiResponse,
      ListLatestSchedulerRunsApiArg
    >({
      query: () => ({
        url: `/dms/v1/messaging/scheduler/run/latest`,
      }),
      providesTags: (result) =>
        result?.runs
          ? [
              ...result.runs.map(({name}) => ({type: 'SchedulerRuns' as const, id: name})),
              {type: 'SchedulerRuns', id: 'LIST'},
            ]
          : [{type: 'SchedulerRuns', id: 'LIST'}],
    }),
    deleteLatestSchedulerRun: build.mutation<
      DeleteLatestSchedulerRunApiResponse,
      DeleteLatestSchedulerRunApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/scheduler/${queryArg.schedulerName}/run/latest`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'SchedulerRuns', id: queryArg.schedulerName},
        {type: 'SchedulerRuns', id: 'LIST'},
      ],
    }),
    runScheduler: build.mutation<RunSchedulerApiResponse, RunSchedulerApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/scheduler/${queryArg.schedulerName}/run`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'SchedulerRuns', id: queryArg.schedulerName},
        {type: 'SchedulerRuns', id: 'LIST'},
      ],
    }),
    renderCustomerMessageEmailTemplateV2: build.mutation<
      RenderCustomerMessageEmailTemplateV2ResponseBody,
      RenderCustomerMessageEmailTemplateV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/messaging/customer-message/${queryArg.customerMessageId}/email/render`,
        method: 'POST',
        body: queryArg.renderCustomerMessageRequestBodyV2,
      }),
    }),
    getMessagingAllowlist: build.query<
      GetMessagingAllowlistApiResponse,
      GetMessagingAllowlistApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/allowlist/${queryArg.channel}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'MessagingAllowlist', id: queryArg.channel},
      ],
    }),
    updateMessagingAllowlist: build.mutation<
      UpdateMessagingAllowlistApiResponse,
      UpdateMessagingAllowlistApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/allowlist/${queryArg.channel}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess<
        UpdateMessagingAllowlistApiResponse,
        UpdateMessagingAllowlistApiArg
      >((_result, queryArg) => [{type: 'MessagingAllowlist', id: queryArg.channel}]),
    }),
    listUserMailboxes: build.query<ListUserMailboxesApiResponse, ListUserMailboxesApiArg>({
      query: () => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox`,
      }),
      providesTags: ['UserMailbox'],
    }),
    listCustomerMessages: build.query<ListCustomerMessagesApiResponse, ListCustomerMessagesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/customerV2/${queryArg.customerId}/customer-message`,
        params: {limit: queryArg.limit, offset: queryArg.offset},
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'CustomerMessage', id: queryArg.customerId},
      ],
    }),
    createGoogleUserMailbox: build.mutation<
      CreateGoogleUserMailboxApiResponse,
      CreateGoogleUserMailboxApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/google`,
        method: 'POST',
        body: queryArg.createUserGoogleMailboxRequestBody,
      }),
      invalidatesTags: ['UserMailbox'],
    }),
    createMicrosoftUserMailbox: build.mutation<
      CreateMicrosoftUserMailboxApiResponse,
      CreateMicrosoftUserMailboxApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/microsoft`,
        method: 'POST',
        body: queryArg.createUserMicrosoftMailboxRequestBody,
      }),
      invalidatesTags: ['UserMailbox'],
    }),
    createImapUserMailbox: build.mutation<
      CreateImapUserMailboxApiResponse,
      CreateImapUserMailboxApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/imap`,
        method: 'POST',
        body: queryArg.createUserImapMailboxRequestBody,
      }),
      invalidatesTags: ['UserMailbox'],
    }),
    updateImapUserMailbox: build.mutation<
      UpdateImapUserMailboxApiResponse,
      UpdateImapUserMailboxApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/imap/${queryArg.mailboxId}`,
        method: 'PUT',
        body: queryArg.updateUserImapMailboxRequestBody,
      }),
      invalidatesTags: ['UserMailbox'],
    }),
    deleteUserMailbox: build.mutation<DeleteUserMailboxApiResponse, DeleteUserMailboxApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/${queryArg.mailboxId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserMailbox'],
    }),
    reauthenticateUserMailbox: build.mutation<
      ReauthenticateUserMailboxApiResponse,
      ReauthenticateUserMailboxApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/${queryArg.mailboxId}/reauthenticate`,
        method: 'POST',
        body: queryArg.reauthenticateUserMailboxRequestBody,
      }),
      invalidatesTags: ['UserMailbox'],
    }),
    getUserMailboxConnectionStatus: build.query<
      GetUserMailboxConnectionStatusApiResponse,
      GetUserMailboxConnectionStatusApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/messaging/email/sync/user-mailbox/${queryArg.mailboxId}/connection-status`,
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'UserMailbox', id: queryArg.mailboxId}],
    }),
  }),
});

export const {
  useRenderCustomerMessageEmailTemplateV2Mutation,
  usePatchOAuthEmailCredentialsMutation,
  useListLatestSchedulerRunsQuery,
  useDeleteLatestSchedulerRunMutation,
  useRunSchedulerMutation,
  useGetCustomerMessageDefinitionQuery,
  useRenderRentalOrderEmailTemplateMutation,
  useRenderRentalOrderEmailTemplateExampleMutation,
  useRenderRentalOrderSmsTemplateMutation,
  useRenderRentalOrderSmsTemplateExampleMutation,
  useRenderVehicleEmailTemplateMutation,
  useRenderVehicleEmailTemplateExampleMutation,
  useRenderVehicleSmsTemplateMutation,
  useRenderVehicleSmsTemplateExampleMutation,
  useRenderCustomerMessageEmailTemplateMutation,
  useRenderCustomerMessageSmsTemplateMutation,
  useSendCustomerSmsMutation,
  useSendWhatsAppContentMessageMutation,
  useGetLastKnownSmtpCredentialsQuery,
  useLazyGetLastKnownSmtpCredentialsQuery,
  useDiscoverEmailProviderMutation,
  useListEmailProviderQuery,
  useStartMicrosoftOAuth2Mutation,
  useCompleteMicrosoftOAuth2Mutation,
  useCreateMicrosoftOAuth2CredentialsMutation,
  useLazyListEmailCredentialsQuery,
  useCreateEmailSmtpCredentialsMutation,
  useDeleteEmailCredentialsMutation,
  useListEmailCredentialsQuery,
  usePatchEmailSmtpCredentialsMutation,
  useCreateSignatureMutation,
  useSendCustomerEmailMutation,
  useListSignaturesQuery,
  useRemoveSignatureMutation,
  useUpdateSignatureMutation,
  useGetNotificationDefinitionsQuery,
  useGetNotificationSettingsQuery,
  usePatchUserNotificationSettingsMutation,
  usePatchTenantNotificationSettingsMutation,
  usePreviewEmailNotificationQuery,
  useGetToastNotificationExampleQuery,
  useListNotificationDefinitionsQuery,
  useListSentToastNotificationsQuery,
  useGetSentToastNotificationQuery,
  useLazyGetSentBackgroundNotificationQuery,
  useLazyGetSentToastNotificationQuery,
  usePatchUnreadSentToastNotificationsMutation,
  usePatchSentToastNotificationMutation,
  useListContextParametersQuery,
  useCreateTemplateMutation,
  useGetTemplateQuery,
  usePatchTemplateMutation,
  useDeleteTemplateMutation,
  useRenderCustomerEmailTemplateExampleMutation,
  useRenderCustomerEmailTemplateMutation,
  useRenderCustomerSmsTemplateExampleMutation,
  useRenderCustomerSmsTemplateMutation,
  useRenderBusinessCaseEmailTemplateMutation,
  useRenderBusinessCaseSmsTemplateMutation,
  useRenderBusinessCaseEmailTemplateExampleMutation,
  useRenderBusinessCaseSmsTemplateExampleMutation,
  useRenderServiceOrderEmailTemplateExampleMutation,
  useRenderServiceOrderEmailTemplateMutation,
  useRenderServiceOrderSmsTemplateExampleMutation,
  useRenderServiceOrderSmsTemplateMutation,
  useGetContextTemplateListQuery,
  useGetMessagingAllowlistQuery,
  useUpdateMessagingAllowlistMutation,
  useListUserMailboxesQuery,
  useListCustomerMessagesQuery,
  useCreateGoogleUserMailboxMutation,
  useCreateMicrosoftUserMailboxMutation,
  useCreateImapUserMailboxMutation,
  useUpdateImapUserMailboxMutation,
  useDeleteUserMailboxMutation,
  useReauthenticateUserMailboxMutation,
  useGetUserMailboxConnectionStatusQuery,
} = messagingApi;
